import { useEffect } from 'react';
import './XScissorAnimation.css';

/**
 * PROFESSIONAL X-SCISSOR ANIMATION
 * Bug-type physical move with crossing claw mechanics
 * 
 * Key Features:
 * - Bug-type physical attack (80 base power, 100% accuracy)
 * - Signature crossing claw attack with precise timing
 * - Sharp cutting effects with bug energy
 * - Professional gaming company quality with martial precision
 * - Physical move animation with crossing slash mechanics
 */
export default function XScissorAnimation({ playerRef, opponentRef, onComplete }) {
  useEffect(() => {
    const playerEl = playerRef.current;
    const opponentEl = opponentRef.current;
    if (!playerEl || !opponentEl) return;

    // Get battle container for positioning
    const container = playerEl.closest('.battle-screen') || playerEl.closest('.battle-container') || document.body;
    const containerRect = container.getBoundingClientRect();
    
    // Calculate positions
    const playerRect = playerEl.getBoundingClientRect();
    const opponentRect = opponentEl.getBoundingClientRect();
    
    const playerX = Math.round((playerRect.left + playerRect.width / 2) - containerRect.left);
    const playerY = Math.round((playerRect.top + playerRect.height / 2) - containerRect.top);
    const opponentX = Math.round((opponentRect.left + opponentRect.width / 2) - containerRect.left);
    const opponentY = Math.round((opponentRect.top + opponentRect.height / 2) - containerRect.top);

    // Calculate attack trajectory
    const dx = opponentX - playerX;
    const dy = opponentY - playerY;
    const distance = Math.hypot(dx, dy);

    // Elements to clean up
    const elements = [];

    // PHASE 1: Claw Preparation (0-500ms)
    console.log('X-Scissor Phase 1: Claw Preparation');
    
    // Player claw preparation stance
    playerEl.classList.add('x-scissor-user-preparation');
    
    // Bug energy aura around player
    const bugAura = document.createElement('div');
    bugAura.className = 'x-scissor-bug-aura';
    bugAura.style.left = `${playerX - 80}px`;
    bugAura.style.top = `${playerY - 80}px`;
    bugAura.style.width = '160px';
    bugAura.style.height = '160px';
    container.appendChild(bugAura);
    elements.push(bugAura);

    // Claw energy particles around player
    for (let i = 0; i < 16; i++) {
      const clawParticle = document.createElement('div');
      clawParticle.className = 'x-scissor-claw-particle';
      const angle = (i / 16) * 360;
      const radius = 60 + (i % 4) * 10;
      const particleX = playerX + Math.cos(angle * Math.PI / 180) * radius;
      const particleY = playerY + Math.sin(angle * Math.PI / 180) * radius;
      clawParticle.style.left = `${particleX - 4}px`;
      clawParticle.style.top = `${particleY - 4}px`;
      clawParticle.style.animationDelay = `${i * 25}ms`;
      clawParticle.textContent = '🪲';
      container.appendChild(clawParticle);
      elements.push(clawParticle);
    }

    // Claw sharpening effects
    for (let i = 0; i < 8; i++) {
      const sharpening = document.createElement('div');
      sharpening.className = 'x-scissor-claw-sharpening';
      sharpening.style.left = `${playerX + (i % 2 === 0 ? -30 : 30)}px`;
      sharpening.style.top = `${playerY + (i * 8) - 30}px`;
      sharpening.style.animationDelay = `${i * 50}ms`;
      sharpening.textContent = '✨';
      container.appendChild(sharpening);
      elements.push(sharpening);
    }

    // PHASE 2: Crossing Claw Attack (500-900ms)
    const phase2Timer = setTimeout(() => {
      console.log('X-Scissor Phase 2: Crossing Claw Attack');
      
      // Player crossing attack movement
      playerEl.classList.remove('x-scissor-user-preparation');
      playerEl.classList.add('x-scissor-user-attack');

      // Create first claw slash (diagonal from top-left to bottom-right)
      const clawSlash1 = document.createElement('div');
      clawSlash1.className = 'x-scissor-claw-slash-1';
      clawSlash1.style.left = `${opponentX - 60}px`;
      clawSlash1.style.top = `${opponentY - 60}px`;
      clawSlash1.style.width = '120px';
      clawSlash1.style.height = '120px';
      container.appendChild(clawSlash1);
      elements.push(clawSlash1);

      // Create second claw slash (diagonal from top-right to bottom-left) - delayed
      const slash2Timer = setTimeout(() => {
        const clawSlash2 = document.createElement('div');
        clawSlash2.className = 'x-scissor-claw-slash-2';
        clawSlash2.style.left = `${opponentX - 60}px`;
        clawSlash2.style.top = `${opponentY - 60}px`;
        clawSlash2.style.width = '120px';
        clawSlash2.style.height = '120px';
        container.appendChild(clawSlash2);
        elements.push(clawSlash2);
      }, 150);
      elements.push({ timer: slash2Timer, type: 'timer' });

      // Crossing energy trails
      for (let i = 0; i < 6; i++) {
        const trailTimer = setTimeout(() => {
          const energyTrail = document.createElement('div');
          energyTrail.className = 'x-scissor-energy-trail';
          const progress = i / 6;
          const trailX = playerX + (dx * progress * 0.8);
          const trailY = playerY + (dy * progress * 0.8);
          energyTrail.style.left = `${trailX - 10}px`;
          energyTrail.style.top = `${trailY - 10}px`;
          energyTrail.style.width = '20px';
          energyTrail.style.height = '20px';
          energyTrail.style.animationDelay = `${i * 30}ms`;
          container.appendChild(energyTrail);
          elements.push(energyTrail);
        }, i * 40);
        elements.push({ timer: trailTimer, type: 'timer' });
      }

      // Bug energy discharge
      for (let i = 0; i < 10; i++) {
        const discharge = document.createElement('div');
        discharge.className = 'x-scissor-bug-discharge';
        const dischargeAngle = (i / 10) * 360;
        const dischargeRadius = 35;
        const dischargeX = playerX + Math.cos(dischargeAngle * Math.PI / 180) * dischargeRadius;
        const dischargeY = playerY + Math.sin(dischargeAngle * Math.PI / 180) * dischargeRadius;
        discharge.style.left = `${dischargeX - 6}px`;
        discharge.style.top = `${dischargeY - 6}px`;
        discharge.style.animationDelay = `${i * 30}ms`;
        discharge.textContent = '💥';
        container.appendChild(discharge);
        elements.push(discharge);
      }
    }, 500);
    elements.push({ timer: phase2Timer, type: 'timer' });

    // PHASE 3: Crossing Impact (900-1300ms)
    const phase3Timer = setTimeout(() => {
      console.log('X-Scissor Phase 3: Crossing Impact');
      
      // Opponent crossing hit reaction
      opponentEl.classList.add('x-scissor-opponent-hit');

      // Reset player position
      playerEl.classList.remove('x-scissor-user-attack');
      playerEl.classList.add('x-scissor-user-recovery');

      // Crossing impact explosion
      const crossingImpact = document.createElement('div');
      crossingImpact.className = 'x-scissor-crossing-impact';
      crossingImpact.style.left = `${opponentX - 100}px`;
      crossingImpact.style.top = `${opponentY - 100}px`;
      crossingImpact.style.width = '200px';
      crossingImpact.style.height = '200px';
      container.appendChild(crossingImpact);
      elements.push(crossingImpact);

      // Crossing cut marks (X pattern)
      const cutMarkX1 = document.createElement('div');
      cutMarkX1.className = 'x-scissor-cut-mark-x1';
      cutMarkX1.style.left = `${opponentX - 80}px`;
      cutMarkX1.style.top = `${opponentY - 80}px`;
      cutMarkX1.style.width = '160px';
      cutMarkX1.style.height = '160px';
      container.appendChild(cutMarkX1);
      elements.push(cutMarkX1);

      const cutMarkX2 = document.createElement('div');
      cutMarkX2.className = 'x-scissor-cut-mark-x2';
      cutMarkX2.style.left = `${opponentX - 80}px`;
      cutMarkX2.style.top = `${opponentY - 80}px`;
      cutMarkX2.style.width = '160px';
      cutMarkX2.style.height = '160px';
      container.appendChild(cutMarkX2);
      elements.push(cutMarkX2);

      // Bug energy burst particles
      for (let i = 0; i < 20; i++) {
        const burstParticle = document.createElement('div');
        burstParticle.className = 'x-scissor-bug-burst';
        const burstAngle = (i / 20) * 360;
        const burstRadius = 70 + Math.random() * 50;
        const burstX = opponentX + Math.cos(burstAngle * Math.PI / 180) * burstRadius;
        const burstY = opponentY + Math.sin(burstAngle * Math.PI / 180) * burstRadius;
        burstParticle.style.left = `${burstX - 5}px`;
        burstParticle.style.top = `${burstY - 5}px`;
        burstParticle.style.animationDelay = `${i * 20}ms`;
        burstParticle.style.setProperty('--burst-x', `${Math.cos(burstAngle * Math.PI / 180) * 40}px`);
        burstParticle.style.setProperty('--burst-y', `${Math.sin(burstAngle * Math.PI / 180) * 40}px`);
        burstParticle.textContent = '🪲';
        container.appendChild(burstParticle);
        elements.push(burstParticle);
      }

      // Cutting shockwaves
      for (let i = 0; i < 3; i++) {
        const shockwaveTimer = setTimeout(() => {
          const shockwave = document.createElement('div');
          shockwave.className = 'x-scissor-cutting-shockwave';
          shockwave.style.left = `${opponentX - 80}px`;
          shockwave.style.top = `${opponentY - 80}px`;
          shockwave.style.width = '160px';
          shockwave.style.height = '160px';
          shockwave.style.animationDelay = `${i * 100}ms`;
          container.appendChild(shockwave);
          elements.push(shockwave);
        }, i * 120);
        elements.push({ timer: shockwaveTimer, type: 'timer' });
      }

      // Sharp cutting effects
      for (let i = 0; i < 12; i++) {
        const cutting = document.createElement('div');
        cutting.className = 'x-scissor-sharp-cutting';
        const cuttingX = opponentX + (Math.random() - 0.5) * 120;
        const cuttingY = opponentY + (Math.random() - 0.5) * 100;
        cutting.style.left = `${cuttingX - 4}px`;
        cutting.style.top = `${cuttingY - 4}px`;
        cutting.style.animationDelay = `${i * 35}ms`;
        cutting.textContent = '✂️';
        container.appendChild(cutting);
        elements.push(cutting);
      }
    }, 900);
    elements.push({ timer: phase3Timer, type: 'timer' });

    // PHASE 4: Cutting Aftermath (1300-1600ms)
    const phase4Timer = setTimeout(() => {
      console.log('X-Scissor Phase 4: Cutting Aftermath');
      
      // Remove recovery effects
      playerEl.classList.remove('x-scissor-user-recovery');
      
      // Cutting residue effects
      for (let i = 0; i < 10; i++) {
        const residue = document.createElement('div');
        residue.className = 'x-scissor-cutting-residue';
        const residueX = opponentX + (Math.random() - 0.5) * 150;
        const residueY = opponentY + (Math.random() - 0.5) * 120;
        residue.style.left = `${residueX - 6}px`;
        residue.style.top = `${residueY - 6}px`;
        residue.style.animationDelay = `${i * 25}ms`;
        residue.textContent = '✨';
        container.appendChild(residue);
        elements.push(residue);
      }
      
      // Bug energy dissipation
      const bugDissipation = document.createElement('div');
      bugDissipation.className = 'x-scissor-bug-dissipation';
      bugDissipation.style.left = `${opponentX - 70}px`;
      bugDissipation.style.top = `${opponentY - 70}px`;
      bugDissipation.style.width = '140px';
      bugDissipation.style.height = '140px';
      container.appendChild(bugDissipation);
      elements.push(bugDissipation);
    }, 1300);
    elements.push({ timer: phase4Timer, type: 'timer' });

    // CLEANUP (1600ms)
    const cleanupTimer = setTimeout(() => {
      console.log('X-Scissor: Animation Complete - Cleanup');
      
      // Remove all CSS classes
      playerEl.classList.remove('x-scissor-user-preparation', 'x-scissor-user-attack', 'x-scissor-user-recovery');
      opponentEl.classList.remove('x-scissor-opponent-hit');
      
      // Remove all created elements
      elements.forEach(item => {
        if (item.type === 'timer') {
          clearTimeout(item.timer);
        } else if (item && item.parentNode) {
          item.remove();
        }
      });
      
      // Animation complete
      onComplete();
    }, 1600);

    // Cleanup function for component unmount
    return () => {
      clearTimeout(cleanupTimer);
      playerEl.classList.remove('x-scissor-user-preparation', 'x-scissor-user-attack', 'x-scissor-user-recovery');
      opponentEl.classList.remove('x-scissor-opponent-hit');
      elements.forEach(item => {
        if (item.type === 'timer') {
          clearTimeout(item.timer);
        } else if (item && item.parentNode) {
          item.remove();
        }
      });
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
}

// Set move type for battle system
XScissorAnimation.moveType = 'bug';
